/**
 * What the media gallery shows, and in what order.
 *
 * Pure, because the two things most easily got wrong here are both decisions rather than rendering:
 * which items are openable, and what index an opened item has.
 */

import type { RobotMediaItem } from '../../../services/api/domains/robots.api'

/** Items split by whether they can be opened right now. */
export interface MediaSections {
  /** Stored and resolvable — the thumbnails an operator can click. */
  stored: RobotMediaItem[]
  /** Held on the device. Visible so it can be requested, but nothing to open yet. */
  onDevice: RobotMediaItem[]
}

/**
 * Split the list into what can be opened and what must be fetched first.
 *
 * A row with no `key` has no object behind it — it is a clip the device is still holding. Rendering
 * it as a thumbnail would offer a click that cannot do anything.
 */
export function splitMedia(items: readonly RobotMediaItem[]): MediaSections {
  const stored: RobotMediaItem[] = []
  const onDevice: RobotMediaItem[] = []

  for (const item of items) {
    if (item.status === 'stored' && item.key) stored.push(item)
    else onDevice.push(item)
  }

  return { stored, onDevice }
}

/**
 * Items a lightbox can page through — and the ONLY list an index may be taken against.
 *
 * This is the trap the map's attachment grid already solves and that a mixed photo/clip gallery
 * walks straight into: if the index is computed against the full list but the lightbox pages the
 * filtered one, opening the third thumbnail shows a different item entirely. Compute both from
 * here, never from the raw array.
 */
export function openableMedia(items: readonly RobotMediaItem[]): RobotMediaItem[] {
  return splitMedia(items).stored
}

/** The index of one item within {@link openableMedia}, or -1. */
export function openableIndexOf(items: readonly RobotMediaItem[], id: string): number {
  return openableMedia(items).findIndex(i => i.id === id)
}

/** Whether the browser can play this inline at all. */
export function isPlayable(item: RobotMediaItem): boolean {
  return item.kind === 'image' || item.kind === 'video'
}

/**
 * A size a person can read, for a Fetch button that is about to spend someone else's data.
 *
 * The button must state the cost BEFORE it is paid: the upload path allows a clip up to 512 MB, and
 * over 4G that is slow, expensive, and charged to the worker wearing the device.
 */
export function formatBytes(bytes: number | undefined): string | null {
  if (typeof bytes !== 'number' || !Number.isFinite(bytes) || bytes < 0) return null
  if (bytes < 1024) return `${bytes} B`

  const units = ['KB', 'MB', 'GB']
  let value = bytes / 1024
  let unit = 0

  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit += 1
  }

  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`
}

/** Clip length as `m:ss`, or null when the device reported none. */
export function formatDuration(ms: number | undefined): string | null {
  if (typeof ms !== 'number' || !Number.isFinite(ms) || ms <= 0) return null

  const total = Math.round(ms / 1000)

  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`
}
